// Panel — the shared on-brand surface/card primitive.
// WL-001 (Fable Hail Mary). The app had Button/Input/TextView/DeleteModal but no
// generic card/panel surface, so screens re-derived white-box styling with ad-hoc
// literals. This composes those values from the design tokens (single source of
// truth) so every Wave-2 card looks the same.
//
// Props:
//   padding   token key of `spacing` (default "xl" = 24px) or any CSS value
//   radius    token key of `radius`  (default "md" = 8px)   or any CSS value
//   elevation token key of `shadow`  (default "e1") or "none" / any CSS value
//   bordered  add a 1px hairline border (colors.lightBorder) — default true
//   as        underlying element/tag (default "div"); use "section"/"article" etc.
//   interactive  adds hover elevation + pointer affordance (for clickable cards)
//
// Example:
//   <Panel>…</Panel>
//   <Panel padding="lg" elevation="e2" as="section">…</Panel>
//   <Panel interactive onClick={…} role="button" tabIndex={0}>…</Panel>

import React from "react";
import styled from "styled-components";
import { colors } from "../../utils";
import { spacing, radius as radiusTokens, shadow, motion } from "../../theme/tokens";

const resolve = (map, key, fallback) => {
  if (key == null) return fallback;
  if (Object.prototype.hasOwnProperty.call(map, key)) return map[key];
  return key; // allow a raw CSS value passthrough
};

const px = (v) => (typeof v === "number" ? `${v}px` : v);

const StyledPanel = styled.div`
  background-color: ${colors.white};
  border-radius: ${(p) => px(resolve(radiusTokens, p.$radius, radiusTokens.md))};
  padding: ${(p) => px(resolve(spacing, p.$padding, spacing.xl))};
  box-shadow: ${(p) => resolve(shadow, p.$elevation, shadow.e1)};
  border: ${(p) => (p.$bordered ? `1px solid ${colors.lightBorder}` : "none")};
  box-sizing: border-box;
  transition: box-shadow ${motion.duration.base} ${motion.easing.standard};

  ${(p) =>
    p.$interactive
      ? `cursor: pointer;
         &:hover { box-shadow: ${shadow.e2}; }
         &:focus-visible { outline: none; box-shadow: ${shadow.focus}; }`
      : ""}
`;

const Panel = ({
  padding = "xl",
  radius = "md",
  elevation = "e1",
  bordered = true,
  interactive = false,
  as,
  children,
  ...rest
}) => (
  <StyledPanel
    as={as}
    $padding={padding}
    $radius={radius}
    $elevation={elevation}
    $bordered={bordered}
    $interactive={interactive}
    {...rest}
  >
    {children}
  </StyledPanel>
);

export default Panel;
